import React, { Suspense, useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { App as AntdApp, ConfigProvider, Grid, theme } from 'antd';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import './App.css';
import Router from './Routes';
import { GlobalProvider } from './Components/Shared/GlobalContext/GlobalContext';
import LoadingOverlay from './Components/Shared/LoadingOverlay/LoadingOverlay';
import { useTheme } from './Components/Shared/GlobalContext/ThemeContext';
import { useTranslation } from 'react-i18next';
import enUS from 'antd/es/locale/en_US';
import frFR from 'antd/es/locale/fr_FR';
import esES from 'antd/es/locale/es_ES';
import deDE from 'antd/es/locale/de_DE';
import zhCN from 'antd/es/locale/zh_CN';
import arEG from 'antd/es/locale/ar_EG';
import i18n from './i18n';
import { RTL_LANGUAGES } from './Constants/language';

const antdLocales = {
  en: enUS,
  fr: frFR,
  es: esES,
  de: deDE,
  zh: zhCN,
  ar: arEG
};
const { useBreakpoint } = Grid;

function App() {
  const { t } = useTranslation();
  const [antdLocale, setAntdLocale] = useState(antdLocales.en);
  const [direction, setDirection] = useState('ltr');

  const { isDarkMode } = useTheme();
  const screens = useBreakpoint() || {};
  const isMobile = !!screens?.xs;

  // Global error handler - logs errors to console without breaking UI
  useEffect(() => {
    const handleError = (event) => {
      console.error('Global JavaScript Error:', event.error);
      console.error('Error details:', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack
      });
      // Prevent the default browser error handling
      event.preventDefault();
    };

    const handleUnhandledRejection = (event) => {
      console.error('Unhandled Promise Rejection:', event.reason);
      // Prevent the default browser unhandled rejection handling
      event.preventDefault();
    };

    // Add global error event listeners
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Capacitor Hardware Back Button Handling
    let backButtonListener;
    if (Capacitor.isNativePlatform()) {
      // 1. Initial StatusBar setup - done once on mount to avoid conflicts during theme switches
      StatusBar.show().catch(e => console.error('StatusBar show error:', e));
      StatusBar.setOverlaysWebView({ overlay: false }).catch(e => console.error('StatusBar overlay error:', e));

      backButtonListener = CapApp.addListener('backButton', ({ canGoBack }) => {
        if (!canGoBack) {
          // No more history: exit the app
          CapApp.exitApp();
        } else {
          // A modal is likely open or we have page history.
          // Calling window.history.back() triggers the real 'popstate' event
          // which your existing useMobileFullscreenModalBackHandler hook is listening for.
          window.history.back();
        }
      });
    }

    // Cleanup function
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      if (backButtonListener) {
        backButtonListener.remove();
      }
    };
  }, []);

  const { darkAlgorithm, defaultAlgorithm } = theme;
  const themeConfig = {
    algorithm: isDarkMode
      ? darkAlgorithm
      : defaultAlgorithm,
    token: {
      primaryColor: '#7F56D9',
    },
    components: {
      Card: {
        padding: screens.md ? 12 : 24,
      },
      Modal: {
        borderRadiusLG: isMobile ? 0 : 8,
      }
    },
  };

  useEffect(() => {
    const updateAntdLocale = () => {
      const lang = i18n.language.split('-')[0];
      setAntdLocale(antdLocales[lang] || antdLocales.en);

      setDirection(RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr');
    };
    updateAntdLocale();
    i18n.on('languageChanged', updateAntdLocale);
    return () => i18n.off('languageChanged', updateAntdLocale);
  }, []);

  // Update the root element's data-theme attribute and StatusBar when dark mode changes
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');

    if (Capacitor.getPlatform() === 'android') {
      const updateStatusBar = async () => {
        const bgColor = isDarkMode ? '#121212' : '#ffffff';
        const style = isDarkMode ? Style.Dark : Style.Default;

        try {
          // Standard Mode: Overlay is FALSE
          // The WebView sits BELOW the status bar, so no CSS padding is needed.
          await StatusBar.setOverlaysWebView({ overlay: false });
          // DEBUG: Using Green to verify if the plugin can paint AT ALL
          // await StatusBar.setBackgroundColor({ color: '#00FF00' });
          await StatusBar.setStyle({ style });
        } catch (e) {
          console.error('[StatusBar] Error:', e);
        }
      };

      updateStatusBar();
    }
  }, [isDarkMode]);

  return (
    <ConfigProvider
      theme={themeConfig}
      locale={antdLocale}
      direction={direction}
    >
      <AntdApp>
        <Suspense fallback={t('loading...')}>
          <GlobalProvider>
            <LoadingOverlay />
            <RouterProvider router={Router} />
          </GlobalProvider>
        </Suspense>
      </AntdApp>
    </ConfigProvider>
  );
}

export default App;
